/**
 * One project, as a card.
 *
 * **This card fetches nothing.** It used to ask for the project's capture
 * sessions and its coverage analysis, which was two requests per card - a
 * hundred requests to paint a fifty-project library. Everything it shows now,
 * including the evaluated workflow behind its status line and its next action,
 * arrives with the list itself, so the grid is one request no matter how many
 * cards are in it.
 *
 * The thumbnail is the project's own first photograph, and it costs the
 * listing nothing: `cover_media_id` arrives with the list row, so the browser
 * fetches the image itself from the same ownership-checked preview route the
 * capture screens use. No card makes an API call to find out what to show.
 *
 * There is still no placeholder. A project with no photo, or one whose preview
 * fails to load, gets no image block at all - a grey rectangle shaped like a
 * picture invites the user to believe the system holds something it does not.
 *
 * ## What the card is allowed to claim
 *
 * - **Coverage** is shown only where a coverage model exists. A room project
 *   gets its subject's own line, never a head-coverage verdict and never a
 *   greyed-out one implying it failed a test it was never given.
 * - **Reconstruction** reads the real status vocabulary. "Seyrek 3D hazır"
 *   requires a sparse artifact, not a succeeded job.
 * - **"3D Model hazır" cannot appear here at all** unless the workflow says
 *   `has_final_model`, which the backend sets from a validated mesh artifact.
 *
 * ## The whole card is the link
 *
 * A single anchor over the title, extended to the card by a stretched
 * pseudo-element, so there is one tab stop and one accessible name. The menu
 * button and the footer link sit above it on the z-axis, which is what keeps
 * them clickable without nesting one interactive element inside another.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "@/api/config";
import type { Project, ProjectListItem } from "@/api/types";
import { IconCamera, IconClock, IconTarget } from "@/components/icons";
import { StatusBadge } from "@/components/ui";
import { COVERAGE_STATUS_TERMS, subjectTypeLabel } from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import { paths } from "@/app/paths";
import { NextActionPanel } from "@/features/workflow/WorkflowViews";
import { DeleteProjectDialog, deletionNeedsTypedName } from "./DeleteProjectDialog";
import { EditProjectDialog } from "./EditProjectDialog";
import { reconstructionStateLabel } from "./projectState";
import "./projects.css";
import "@/features/workflow/workflow.css";

/**
 * The card's overflow menu.
 *
 * Three items, and all three are real: rename and description both persist
 * through `PATCH /projects/{id}`, and delete uses the existing hard-delete
 * path with its confirmation. "Arşivle" is still absent - `Project.status`
 * has an `archived` value that nothing in the product sets or reads, so the
 * control would do nothing.
 *
 * Keyboard behaviour is the menu pattern the platform would have given a
 * native control: Escape closes and returns focus to the trigger, ArrowDown
 * from the trigger opens and lands on the first item, and Home/End jump.
 */
function CardMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    // Focus lands inside the menu, so the next Tab is within it rather than
    // continuing through the card behind it.
    items.current?.querySelector<HTMLButtonElement>("[role='menuitem']")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onClick = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const buttons = Array.from(
      items.current?.querySelectorAll<HTMLButtonElement>("[role='menuitem']") ?? [],
    );
    if (buttons.length === 0) return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      buttons[(index + 1) % buttons.length]!.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]!.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      buttons[0]!.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      buttons[buttons.length - 1]!.focus();
    }
  }

  return (
    <div className="card-menu" ref={root}>
      <button
        type="button"
        className="card-menu__button"
        ref={trigger}
        aria-label="Proje işlemleri"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        ⋯
      </button>
      {open && (
        <div className="card-menu__panel" role="menu" ref={items} onKeyDown={onMenuKeyDown}>
          <button
            type="button"
            role="menuitem"
            className="card-menu__item"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            Düzenle
          </button>
          <button
            type="button"
            role="menuitem"
            className="card-menu__item card-menu__item--danger"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            Sil
          </button>
        </div>
      )}
    </div>
  );
}

export function ProjectCard({
  project,
  onDeleted,
  onUpdated,
}: {
  project: ProjectListItem;
  onDeleted?: () => void;
  /** Called after a successful edit so the caller can re-read the list. */
  onUpdated?: (project: Project) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  // A preview the server cannot produce removes the block rather than leaving
  // a broken-image glyph on the card.
  const [coverFailed, setCoverFailed] = useState(false);

  const sessionCount = project.capture_session_count;
  const coverageStatus = project.latest_coverage_status;
  const measured = project.measured_region_count;
  const workflow = project.workflow ?? null;
  // The backend's own answer, which is `false` for every subject type that has
  // no five-region model. Falling back to the coverage status alone would
  // re-derive it on the client and could disagree.
  const hasSemanticCoverage = project.capabilities?.semantic_coverage ?? false;
  const coverId = project.cover_media_id ?? null;

  return (
    <article className="project-card">
      <header className="project-card__head">
        <span className="chip">{subjectTypeLabel(project.subject_type)}</span>
        <CardMenu onEdit={() => setEditing(true)} onDelete={() => setConfirming(true)} />
      </header>

      {confirming && (
        <DeleteProjectDialog
          project={project}
          // Typing the name is asked for only when there is something to lose,
          // and "something" is media rather than an empty capture session.
          requireName={deletionNeedsTypedName(project.media_count)}
          onCancel={() => setConfirming(false)}
          onDeleted={() => {
            setConfirming(false);
            onDeleted?.();
          }}
        />
      )}

      {editing && (
        <EditProjectDialog
          project={project}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => {
            setEditing(false);
            onUpdated?.(saved);
          }}
        />
      )}

      {/*
        Decorative for assistive technology on purpose: the card's accessible
        name is the project name on the link below, and "İlk fotoğraf" read out
        before every title would be noise. It sits under the stretched link, so
        clicking the picture opens the project like the rest of the card.
      */}
      {coverId && !coverFailed && (
        <div className="project-card__cover">
          <img
            src={apiUrl(`/media/${encodeURIComponent(coverId)}/preview`)}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setCoverFailed(true)}
          />
        </div>
      )}

      <h3 className="project-card__name">
        <Link className="project-card__link" to={paths.project(project.id)}>
          {project.name}
        </Link>
      </h3>

      <div className="project-card__badges" aria-label="Proje durumu">
        {hasSemanticCoverage && (coverageStatus ? (
            <StatusBadge tone={COVERAGE_STATUS_TERMS[coverageStatus].tone}>
              {COVERAGE_STATUS_TERMS[coverageStatus].label}
            </StatusBadge>
          ) : (
            <StatusBadge tone="neutral">Henüz analiz edilmedi</StatusBadge>
          ))}
        {/* Success only for an output that genuinely exists; caution only for
            a run that actually failed. Everything else - not started, queued,
            running - is neutral, because none of those is a problem. */}
        <StatusBadge
          tone={
            workflow?.furthest_output !== null && workflow?.furthest_output !== undefined
              ? "success"
              : project.latest_reconstruction_status === "failed"
                ? "danger"
                : "neutral"
          }
        >
          3D oluşturma: {reconstructionStateLabel(project) === "Başlatılmadı" ? "Henüz başlamadı" : reconstructionStateLabel(project)}
        </StatusBadge>
      </div>

      <dl className="project-card__facts">
        {hasSemanticCoverage && <div>
          <dt>
            <IconTarget size={15} /> Kapsama
          </dt>
          {/*
            Region counts only when the analysis says which regions it could
            evaluate. Never "0/5" for a project nobody has analysed - that
            reads as five failures.
          */}
          <dd>
            {!hasSemanticCoverage
              ? "Bu tür için ölçülmüyor"
              : measured !== null
                ? `${measured} bölge ölçüldü`
                : "—"}
          </dd>
        </div>}
        <div>
          <dt>
            <IconCamera size={15} /> Medya
          </dt>
          <dd>
            {project.media_count} dosya · {sessionCount} çekim
          </dd>
        </div>
      </dl>

      {workflow && (
        <NextActionPanel workflow={workflow} projectId={project.id} compact />
      )}

      <footer className="project-card__foot">
        <span className="project-card__when">
          <IconClock size={14} /> Son çalışma: {formatDateTime(project.last_activity_at)}
        </span>
      </footer>
    </article>
  );
}
